import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Heart,
  ShoppingCart,
  X,
  Star,
  Clock,
  Users,
  Search,
  ArrowRight,
  Sparkles,
  Trash2
} from 'lucide-react'

interface Course {
  id: string
  title: string
  description: string
  instructor: string
  thumbnail?: string
  image?: string
  price: number
  originalPrice?: number
  rating: number
  studentsCount: number
  duration: string
  category: string
}

export default function UserWishlist() {
  const navigate = useNavigate()
  const [wishlist, setWishlist] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadWishlist()
  }, [])

  const loadWishlist = () => {
    try {
      const savedWishlist = JSON.parse(localStorage.getItem('userWishlist') || '[]')
      setWishlist(savedWishlist)
    } catch (error) {
      console.error('Error loading wishlist:', error)
      setWishlist([])
    } finally {
      setLoading(false)
    }
  }

  const removeFromWishlist = (e: React.MouseEvent, courseId: string) => {
    e.stopPropagation()
    const updatedWishlist = wishlist.filter(course => course.id !== courseId)
    setWishlist(updatedWishlist)
    localStorage.setItem('userWishlist', JSON.stringify(updatedWishlist))
    window.dispatchEvent(new Event('wishlist-updated'))
  }

  const addToCart = (e: React.MouseEvent, course: Course) => {
    e.stopPropagation()
    const cart = JSON.parse(localStorage.getItem('userCart') || '[]')
    const isInCart = cart.some((item: Course) => item.id === course.id)

    if (!isInCart) {
      const cartItem = {
        ...course,
        thumbnail: course.thumbnail || course.image || ''
      }
      cart.push(cartItem)
      localStorage.setItem('userCart', JSON.stringify(cart))

      // Dispatch event to update cart badge
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: { count: cart.length } }))

      // Show success notification
      const successDiv = document.createElement('div')
      successDiv.className = 'fixed top-4 right-4 bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-2xl z-50 flex items-center gap-3 animate-in fade-in slide-in-from-top-5 duration-300'
      successDiv.innerHTML = `
        <div class="bg-white/20 p-1 rounded-full">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <span class="font-medium">Added to cart</span>
      `
      document.body.appendChild(successDiv)
      setTimeout(() => {
        successDiv.classList.add('animate-out', 'fade-out', 'slide-out-to-top-5')
        setTimeout(() => document.body.removeChild(successDiv), 300)
      }, 2000)
    } else {
      navigate('/user/cart')
    }
  }

  const filteredWishlist = wishlist.filter(course =>
    searchQuery === '' ||
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (course.category && course.category.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-violet-600 rounded-full"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-20">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
            My <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-violet-600">Collection</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 max-w-xl text-lg">
            Your curated list of courses to master beauty and wellness.
          </p>
        </div>

        {/* Search Bar */}
        {wishlist.length > 0 && (
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 to-violet-600 rounded-2xl opacity-20 group-hover:opacity-40 transition duration-500 blur"></div>
            <div className="relative flex items-center bg-white dark:bg-slate-800 rounded-xl p-1">
              <Search className="h-5 w-5 text-slate-400 ml-3" />
              <input
                type="text"
                placeholder="Search collection..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full md:w-64 pl-3 pr-4 py-2 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Wishlist Content */}
      {filteredWishlist.length > 0 ? (
        <div className="grid grid-cols-1 gap-6">
          {filteredWishlist.map((course, index) => (
            <div
              key={course.id}
              onClick={() => navigate(`/user/courses/${course.id}`)}
              className="group relative bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-100 dark:border-slate-700/50 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500 cursor-pointer"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex flex-col md:flex-row h-full">
                {/* Image Section */}
                <div className="md:w-2/5 relative overflow-hidden h-48 md:h-auto">
                  <div className="absolute inset-0 bg-slate-200 dark:bg-slate-700 animate-pulse" />
                  <img
                    src={course.thumbnail || course.image || ''}
                    alt={course.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:bg-gradient-to-r" />

                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md text-xs font-bold uppercase tracking-wider rounded-lg shadow-lg">
                      {course.category || 'General'}
                    </span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-between relative">
                  {/* Remove Button - Absolute Top Right */}
                  <button
                    onClick={(e) => removeFromWishlist(e, course.id)}
                    className="absolute top-4 right-4 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-all opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 duration-300"
                    title="Remove from wishlist"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 px-2 py-0.5 rounded-md border border-yellow-100 dark:border-yellow-900/30">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        <span className="text-xs font-bold ml-1">{course.rating || 4.5}</span>
                      </div>
                      <span className="text-slate-300 dark:text-slate-600">•</span>
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> {course.studentsCount || 0} Students
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors leading-tight">
                      {course.title}
                    </h3>

                    <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2 mb-6 max-w-xl">
                      {course.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-slate-100 dark:border-slate-700/50">
                    <div className="flex flex-col">
                      <span className="text-xs text-slate-400 uppercase font-bold tracking-wider">Price</span>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-black text-slate-900 dark:text-white">₹{course.price.toLocaleString()}</span>
                        {course.originalPrice && (
                          <span className="text-sm text-slate-400 line-through">₹{course.originalPrice.toLocaleString()}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => addToCart(e, course)}
                        className="px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-xl hover:bg-violet-600 dark:hover:bg-violet-300 transition-all shadow-lg hover:shadow-violet-500/25 flex items-center gap-2 group/btn"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>Add to Cart</span>
                        <ArrowRight className="w-4 h-4 opacity-0 -ml-2 group-hover/btn:opacity-100 group-hover/btn:ml-0 transition-all" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 px-4">
          <div className="relative mb-8 group">
            <div className="absolute -inset-4 bg-gradient-to-r from-pink-500 to-violet-600 rounded-full opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-500"></div>
            <div className="relative bg-white dark:bg-slate-800 p-6 rounded-full shadow-xl">
              <Heart className="h-12 w-12 text-rose-500 fill-rose-50" />
            </div>
          </div>

          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3 text-center">
            Your collection is empty
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-center max-w-md mb-8 text-lg">
            {searchQuery
              ? `We couldn't find any courses matching "${searchQuery}"`
              : "Discover and save courses you're interested in. Build your personal learning path."}
          </p>

          <button
            onClick={() => navigate('/user/courses')}
            className="px-8 py-4 bg-gradient-to-r from-violet-600 to-pink-600 text-white rounded-2xl font-bold shadow-xl shadow-violet-500/30 hover:shadow-violet-500/50 hover:scale-105 transition-all flex items-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            Explore Courses
          </button>
        </div>
      )}
    </div>
  )
}